export type DeclaredSub = {
  _id?: string
  name?: string
  result?: string | number
  horse?: string | number
  status?: string
  marketId?: string
  horseBack?: number
  horseLay?: number
  rateBack?: number
  rateLay?: number
  rateCategoryId?: string | { _id?: string; name?: string } | null
  subMarket?: {
    name?: string
    result?: string | number
    status?: string
    horseBack?: number
    horseLay?: number
    rateBack?: number
    rateLay?: number
    marketId?: string
    _id?: string
    rateCategoryId?: string | { _id?: string; name?: string } | null
  }
  [key: string]: unknown
}

export function smOf(row: DeclaredSub): DeclaredSub {
  return (row.subMarket as DeclaredSub | undefined) ?? row
}

export function displayName(row: DeclaredSub): string {
  return String(smOf(row).name ?? row.name ?? '')
}

export function displayResult(row: DeclaredSub): string {
  const sm = smOf(row)
  if (sm.status === 'Void') return 'Void'
  if (sm.result != null && sm.result !== '') return String(sm.result)
  return ''
}

export function runners(row: DeclaredSub): {
  runner1: number | string
  runner2: number | string
  ratesNo: string
} {
  const sm = smOf(row)
  return {
    runner1: sm.horseLay ?? '',
    runner2: sm.horseBack ?? '',
    ratesNo: sm.rateLay != null && sm.rateBack != null ? `${sm.rateLay}/${sm.rateBack}` : '',
  }
}

/**
 * Prefer nested fancy `_id` / `marketId` over the outer event wrapper's `marketId`.
 */
export function resolveRedeclareSubMarketId(row: DeclaredSub | null | undefined): string {
  if (!row) return ''
  const sm = smOf(row)
  return String(sm._id || sm.marketId || row._id || '').trim()
}

export type ResultRunOpt = { runs: number; _id?: string }
export type ResultRateOpt = {
  _id?: string
  rateLay?: number
  rateBack?: number
  rateNo?: string | null
  char?: string | null
  rateCategoryId?: string | { _id?: string; name?: string } | null
}

/** Prod ResultScoreBoard: button text is always `rateLay/rateBack` (ignores char). */
export function formatResultOddsOption(rate: ResultRateOpt): string {
  if (rate.rateLay != null && rate.rateBack != null) {
    return `${rate.rateLay}/${rate.rateBack}`
  }
  const labeled = String(rate.rateNo ?? rate.char ?? '').trim()
  return labeled
}

function categoryIdOf(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    const id = (value as { _id?: unknown })._id
    return id != null ? String(id).trim() : ''
  }
  return String(value).trim()
}

/** Fancy's Rate Category id (Lab ScoreDashboard filter key). */
export function fancyRateCategoryId(row: DeclaredSub): string {
  const sm = smOf(row)
  return categoryIdOf(sm.rateCategoryId) || categoryIdOf(row.rateCategoryId)
}

export function ratePresetCategoryId(rate: ResultRateOpt): string {
  return categoryIdOf(rate.rateCategoryId)
}

/**
 * Lab ResultScoreBoard Rate strip: always the full board `rateList` (all presets).
 * Do not filter by fancy category — that collapsed Results to a single chip when
 * the fancy's category only had one pair (e.g. 100/100).
 */
export function ratesForResultFancy(_row: DeclaredSub, rateList: ResultRateOpt[]): ResultRateOpt[] {
  return Array.isArray(rateList) ? rateList : []
}

export function parseDeclaredListResponse(data: unknown): DeclaredSub[] {
  return parseDeclaredBoardResponse(data).list
}

/**
 * Lab/prod `getAllResultDeclared` → `{ submarketList, runList, rateList }`
 * (ResultDashboard passes rateList/runList into ResultScoreBoard).
 */
export function parseDeclaredBoardResponse(data: unknown): {
  list: DeclaredSub[]
  runList: ResultRunOpt[]
  rateList: ResultRateOpt[]
} {
  if (Array.isArray(data)) {
    return { list: data as DeclaredSub[], runList: [], rateList: [] }
  }
  if (data && typeof data === 'object') {
    const obj = data as {
      submarketList?: DeclaredSub[]
      data?: DeclaredSub[]
      runList?: ResultRunOpt[]
      rateList?: ResultRateOpt[]
    }
    const rows = obj.submarketList ?? obj.data
    return {
      list: Array.isArray(rows) ? rows : [],
      runList: Array.isArray(obj.runList) ? obj.runList : [],
      rateList: Array.isArray(obj.rateList) ? obj.rateList : [],
    }
  }
  return { list: [], runList: [], rateList: [] }
}
